<template>
  <!-- Top of the system page: what the system is, who looks after it, how complete it is, and what you can do (US 9370) -->
  <header class="sx-hero" qid="system-hero">
    <span class="sx-hero__logo" :class="{ 'has-image': system.logo }" aria-hidden="true">
      <img v-if="system.logo" :src="system.logo" alt="">
      <template v-else>{{ initials }}</template>
    </span>

    <div class="sx-hero__main">
      <router-link :to="{ name: 'systems' }" class="sx-hero__back" qid="system-back">
        <font-awesome-icon icon="angle-left"/>{{ $t('systems.title') }}
      </router-link>
      <h1 class="sx-hero__name" qid="p-view-page-title">{{ system.name }}</h1>
      <p v-if="system.description" class="sx-hero__text">{{ system.description | truncate(220) }}</p>
      <div class="sx-hero__tags">
        <span v-if="retired" class="sx-hero__tag is-retired"><font-awesome-icon icon="archive"/>{{ retiredLabel }}</span>
        <span v-if="runsAsLabel" class="sx-hero__tag is-mint"><font-awesome-icon icon="globe"/>{{ runsAsLabel }}</span>
        <span v-if="system.in_use_since && !retired" class="sx-hero__tag"><font-awesome-icon icon="check"/>{{ $t('system_hero.in_use_since', { year: system.in_use_since }) }}</span>
        <span v-if="!system.partition && getClient.parent" class="sx-hero__tag"><font-awesome-icon icon="lock"/>{{ $t('systems_overview.from_workspace', { name: getClient.parent.name }) }}</span>
        <span v-for="tag in shownTags" :key="tag.id || tag.name_translated" class="sx-hero__tag" :title="tag.name_translated"><font-awesome-icon icon="tag"/>{{ tag.name_translated | truncate(30) }}</span>
        <span v-if="hiddenTags" class="sx-hero__tag">+{{ hiddenTags }}</span>
        <slot name="task"></slot>
      </div>
    </div>

    <div class="sx-hero__people" qid="system-owners-list">
      <small>{{ $t('system_hero.people') }}</small>
      <template v-if="people.length">
        <div class="sx-hero__avatars">
          <Avatar v-for="user in people.slice(0, 4)" :key="user.id" :object="user" size="sm" :noOwner="true"></Avatar>
        </div>
        <span class="sx-hero__who">
          {{ owner ? owner.name : $t('system_hero.no_owner') }}
          <span v-if="people.length > 1" class="sx-hero__faint">{{ $t('system_hero.more_people', { count: people.length - 1 }) }}</span>
        </span>
      </template>
      <span v-else class="sx-hero__who is-warn"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('system_hero.nobody') }}</span>
    </div>

    <div v-if="summary" class="sx-hero__done" qid="system-completeness">
      <svg class="sx-hero__ring" viewBox="0 0 76 76" aria-hidden="true">
        <circle cx="38" cy="38" r="32" fill="none" stroke="rgba(255,255,255,.18)" stroke-width="7"/>
        <circle v-if="done"
                cx="38" cy="38" r="32" fill="none" stroke="#7FE0C4" stroke-width="7" stroke-linecap="round"
                transform="rotate(-90 38 38)"
                :stroke-dasharray="ringDash"/>
      </svg>
      <div class="sx-hero__done-text">
        <b>{{ $t('system_hero.filled', { done: done, total: total }) }}</b>
        <span>{{ missingText }}</span>
      </div>
    </div>

    <div class="sx-hero__act">
      <button v-if="getClient.projectModule && canEdit"
              type="button"
              class="sx-hero__btn is-white"
              qid="system-get-updated-hero"
              @click="$emit('get-updated')">
        <font-awesome-icon icon="sync-alt"/>{{ $t('system_page.current.get_updated') }}
      </button>
      <div class="sx-hero__row">
        <button v-if="canEdit"
                type="button"
                class="sx-hero__btn sx-hero__grow"
                qid="edit-tp-modal"
                @click="$emit('edit')">
          <font-awesome-icon icon="pen"/>{{ $t('system_hero.edit') }}
        </button>
        <b-dropdown right
                    no-caret
                    variant="link"
                    toggle-class="sx-hero__btn"
                    menu-class="sx-menu"
                    qid="system-more">
          <template #button-content>
            <font-awesome-icon icon="ellipsis-h"/><span class="sr-only">{{ $t('system_hero.more') }}</span>
          </template>
          <b-dropdown-item v-if="canEdit" @click="$emit('edit')"><font-awesome-icon icon="pen"/>{{ $t('system_hero.menu.edit') }}</b-dropdown-item>
          <b-dropdown-item v-if="canEdit" @click="$emit('move-links')"><font-awesome-icon icon="exchange-alt"/>{{ $t('system_hero.menu.move_links') }}</b-dropdown-item>
          <b-dropdown-item v-if="canCreate" qid="system-duplicate" @click="$emit('duplicate')"><font-awesome-icon icon="copy"/>{{ $t('system_hero.menu.duplicate') }}</b-dropdown-item>
          <b-dropdown-item qid="system-print" @click="print"><font-awesome-icon icon="file-alt"/>{{ $t('system_hero.menu.export') }}</b-dropdown-item>
          <template v-if="canEdit">
            <b-dropdown-item v-if="!retired" @click="$emit('retire')"><font-awesome-icon icon="archive"/>{{ $t('system_hero.menu.retire') }}</b-dropdown-item>
            <b-dropdown-item v-else @click="$emit('reactivate')"><font-awesome-icon icon="rotate-left"/>{{ $t('system_hero.menu.reactivate') }}</b-dropdown-item>
          </template>
          <template v-if="canDelete">
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item link-class="is-danger" qid="delete-s-button" @click="$emit('delete')"><font-awesome-icon icon="trash-alt"/>{{ $t('system_hero.menu.delete') }}</b-dropdown-item>
          </template>
        </b-dropdown>
      </div>
    </div>
  </header>
</template>

<script>
  import moment from 'moment';
  import Avatar from '@/components/Avatar.vue';

  export default {
    name: 'SystemHero',
    components: {
      Avatar
    },
    props: {
      system: { type: Object, required: true },
      // From the system summary: what's required, what's missing, and the contracts it runs on
      summary: { type: Object, default: null }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      editable() {
        return !this.getClient.parent || this.system.partition;
      },
      canEdit() {
        return this.isGranted([this.permissions.SYSTEM_EDIT, this.permissions.SYSTEM_EDIT_OWN], this.system) && this.editable;
      },
      canDelete() {
        return this.isGranted([this.permissions.SYSTEM_DELETE, this.permissions.SYSTEM_DELETE_OWN], this.system) && this.editable;
      },
      canCreate() {
        return this.isGranted(this.permissions.SYSTEM_CREATE);
      },
      initials() {
        let words = (this.system.name || '').trim().split(/\s+/).filter(Boolean);
        if (words.length > 1) {
          return (words[0][0] + words[1][0]).toUpperCase();
        }
        return (words[0] || '').slice(0, 3);
      },
      retired() {
        return this.system.lifecycle_status === 'retired';
      },
      retiredLabel() {
        return this.system.retired_at
          ? this.$t('system_hero.retired_on', { date: moment(this.system.retired_at).format('D MMM YYYY') })
          : this.$t('systems_overview.lifecycle.retired');
      },
      runsAsLabel() {
        if (!this.system.runs_as) {
          return '';
        }
        let label = this.$t('systems_overview.runs_as.' + this.system.runs_as);
        // Systems on your premises keep their own location; SaaS and hosted ones run on a contract
        if (['premises', 'physical'].includes(this.system.runs_as)) {
          return this.system.location ? label + ' · ' + this.system.location : label;
        }
        return this.runsOnContract ? label + ' · ' + this.runsOnContract.name : label;
      },
      runsOnContract() {
        let contracts = (this.summary && this.summary.contracts) || [];
        return contracts.find(link => ['software_hosting', 'hosting'].includes(link.covers)) || null;
      },
      tags() {
        return this.system.tags_translated || [];
      },
      shownTags() {
        return this.tags.slice(0, 3);
      },
      hiddenTags() {
        return Math.max(this.tags.length - 3, 0);
      },
      owner() {
        return (this.system.users || []).find(user => user.id == this.system.owner) || null;
      },
      // The owner first, then the contributors
      people() {
        let users = this.system.users || [];
        return this.owner ? [this.owner].concat(users.filter(user => user.id !== this.owner.id)) : users;
      },
      total() {
        return this.summary.required.length;
      },
      done() {
        return this.total - this.summary.missing.length;
      },
      ringDash() {
        let length = 2 * Math.PI * 32;
        let filled = this.total ? length * this.done / this.total : 0;
        return filled + ' ' + length;
      },
      missingText() {
        if (!this.summary.missing.length) {
          return this.$t('system_hero.all_filled');
        }
        let names = this.summary.missing.map(key => this.$t('systems_overview.missing.' + key).toLowerCase());
        return this.$t('system_hero.missing', { list: names.join(', ') });
      }
    },
    methods: {
      // The page has a print layout without menus, so "Save as PDF" in the print dialog gives a clean copy
      print() {
        window.print();
      }
    }
  }
</script>
